import React from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { HotelInventory } from '@apps/admin/routes/Planning/hooks/HotelListSearcher/useZentrumHubHotelListSearcher';
import { StarRating } from '@shared/components';
import { useHotelRedirectUrl } from '@apps/admin/routes/Planning/routes/Accommodations/hooks/useHotelRedirectUrl';
import { TravelDistancesBetweenPointsResponseFragment, Maybe } from '@graphql/generated';
import { DistanceFromVenue } from '@shared/components/DistanceFromVenue';

interface MatchedHotelCardProps
  extends Readonly<{
    hotel: HotelInventory;
    distances?: Maybe<TravelDistancesBetweenPointsResponseFragment>;
    milesToVenue?: number | null;
  }> {}

export const MatchedHotelCard: React.FC<MatchedHotelCardProps> = ({ hotel, distances, milesToVenue }) => {
  const hotelUrl = useHotelRedirectUrl({
    hotel: {
      zhId: hotel.zhId,
      joyPlaceId: hotel.joyPlaceId,
      address: hotel.address,
      location: hotel.location ? { lat: hotel.location.lat, lng: hotel.location.lon } : undefined,
      displayName: hotel.displayName,
      websiteUrl: hotel.hotelUrl,
    },
    searchParams: {
      utm_medium: 'web',
      utm_source: 'admin_custom_page',
      utm_campaign: 'place_to_stay_component',
    },
  });

  return (
    <Flex
      gap={5}
      alignItems="flex-start"
      width="100%"
      padding={5}
      borderRadius={6}
      border="1px solid"
      borderColor="mono3"
      backgroundColor="white"
      cursor={hotelUrl ? 'pointer' : 'default'}
      as={hotelUrl ? 'a' : 'div'}
      href={hotelUrl}
      target={hotelUrl ? '_blank' : undefined}
      rel={hotelUrl ? 'noopener noreferrer' : undefined}
      _hover={hotelUrl ? { opacity: 0.9 } : undefined}
      transition="opacity 0.2s"
    >
      {hotel.photo && (
        <Box
          width={pxToRem(80)}
          height={pxToRem(80)}
          minWidth={pxToRem(80)}
          minHeight={pxToRem(80)}
          borderRadius={3}
          backgroundImage={`url(${hotel.photo})`}
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
          flexShrink={0}
        />
      )}
      <Flex
        flexDirection="column"
        gap={1}
        flex={1}
        minWidth={0}
      >
        <TextV2
          typographyVariant="hed3"
          fontWeight={600}
        >
          {hotel.displayName}
        </TextV2>
        <Flex
          alignItems="center"
          gap={3}
          marginTop={2}
          flexWrap="wrap"
        >
          {hotel.starRating && (
            <StarRating
              id={`matched-hotel-${hotel.joyPlaceId || 'unknown'}`}
              rating={hotel.starRating}
              showPartialRating
            />
          )}
          <DistanceFromVenue
            milesToVenue={milesToVenue}
            distances={distances}
            color="mono10"
            size="sm"
          />
        </Flex>
        {hotel.address && (
          <TextV2
            typographyVariant="body1"
            color="mono10"
            marginTop={1}
          >
            {hotel.address}
          </TextV2>
        )}
      </Flex>
    </Flex>
  );
};
